import { useSuspenseQuery } from '@tanstack/react-query';
import { fetchTaskById } from 'api/tasks';
import { CampaignResourcePopover } from 'components/tasks/form/links/CampaignResourcePopover';
import { ContactResourcePopover } from 'components/tasks/form/links/ContactResourcePopover';
import { TicketResourcePopover } from 'components/tasks/form/links/TicketResourcePopover';
import { TransactionResourcePopover } from 'components/tasks/form/links/TransactionResourcePopover';
import { LinkedResourcePopoverItem } from 'components/tasks/types';
import { useTaskDrawerState } from 'components/tasks/useTaskDrawerState';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { first } from 'lodash';
import { accountQueryKeys } from 'queries/account-query-keys';
import { TaskLinkable } from 'types/domain';

interface LinkPopoverContentProps {
    resource: LinkedResourcePopoverItem | null;
}
const LinkPopoverContent = ({ resource }: LinkPopoverContentProps) => {
    const { accountId } = useCurrentAccount();
    const { selectedTaskId } = useTaskDrawerState();

    const { data: task } = useSuspenseQuery({
        queryKey: accountQueryKeys.taskById(accountId, selectedTaskId as number),
        queryFn: () => fetchTaskById(accountId, selectedTaskId as number),
    });

    if (!resource) return null;

    const curriedGetLinkedResourceData = <T extends TaskLinkable['linkable_type']>(type: T) =>
        getLinkedResourceData(task.links, resource.id, type);

    switch (resource.type) {
        case 'contact': {
            const link = curriedGetLinkedResourceData('contact');
            return <ContactResourcePopover contact={link?.linkable} />;
        }
        case 'transaction': {
            const link = curriedGetLinkedResourceData('transaction');
            return <TransactionResourcePopover transaction={link?.linkable} />;
        }
        case 'campaign': {
            const link = curriedGetLinkedResourceData('campaign');
            return <CampaignResourcePopover campaign={link?.linkable} />;
        }
        case 'ticket': {
            const link = curriedGetLinkedResourceData('ticket');
            return <TicketResourcePopover ticket={link?.linkable} />;
        }
        default:
            return null;
    }
};

const getLinkedResourceData = <T extends TaskLinkable['linkable_type']>(
    links: TaskLinkable[],
    id: string,
    type: T,
) => {
    return first(links.filter(l => l.linkable_id.toString() === id).filter(isLinkableType(type)));
};

const isLinkableType = <T extends TaskLinkable['linkable_type']>(val: T) => {
    return (obj: TaskLinkable): obj is Extract<TaskLinkable, { linkable_type: T }> =>
        obj.linkable_type === val;
};

export { LinkPopoverContent };
